import { useMemo, useState } from 'react';
import {
  useConstructionTypes,
  useCreateConstructionType,
  useUpdateConstructionType,
  useDeleteConstructionType,
} from '../../../../hooks/useConstructionTypes';
import { usePlans } from '../../../../hooks/usePlans';
import { usePaginatedFilter } from '../../../../common/hooks/usePaginatedFilter';
import {
  ITEMS_PER_PAGE,
  INITIAL_CONSTRUCTION_TYPE_STATE,
} from '../constants/constructionTypesConstants';
import { filterConstructionTypes } from '../utils/constructionTypesUtils';

export const useConstructionTypesTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedConstructionType, setSelectedConstructionType] = useState(
    INITIAL_CONSTRUCTION_TYPE_STATE
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  const { data: constructionTypes = [], isLoading } = useConstructionTypes();
  const { data: plans = [] } = usePlans();

  const createMutation = useCreateConstructionType();
  const updateMutation = useUpdateConstructionType();
  const deleteMutation = useDeleteConstructionType();

  const planNameById = useMemo(
    () =>
      plans.reduce((acc, item) => {
        if (item?.uuid) {
          acc[item.uuid] = item.name;
        }
        return acc;
      }, {}),
    [plans]
  );

  const enrichedConstructionTypes = useMemo(
    () =>
      constructionTypes.map((item) => ({
        ...item,
        plan_name: planNameById[item.uuid_plan] || '-',
      })),
    [constructionTypes, planNameById]
  );

  const filteredByPlan = useMemo(() => {
    if (!selectedPlan) return enrichedConstructionTypes;
    return enrichedConstructionTypes.filter((item) => item.uuid_plan === selectedPlan);
  }, [enrichedConstructionTypes, selectedPlan]);

  const {
    data,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: filteredByPlan,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterConstructionTypes,
  });

  const handleOpenDialog = () => {
    setSelectedConstructionType(INITIAL_CONSTRUCTION_TYPE_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedConstructionType((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlanChange = (event) => {
    setSelectedPlan(event.target.value);
  };

  const handleEdit = (item) => {
    setSelectedConstructionType({
      uuid: item.uuid,
      uuid_plan: item.uuid_plan || '',
      name: item.name || '',
      surface: item.surface ?? '',
      description: item.description || '',
      image_url: item.image_url || '',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      uuid_plan: selectedConstructionType?.uuid_plan || '',
      name: selectedConstructionType?.name || '',
      description: selectedConstructionType?.description || '',
      image_url: selectedConstructionType?.image_url || '',
      surface:
        selectedConstructionType?.surface === '' || selectedConstructionType?.surface == null
          ? 0
          : Number(selectedConstructionType.surface),
    };

    if (editMode) {
      await updateMutation.mutateAsync({
        id: selectedConstructionType.uuid,
        data: payload,
      });
    } else {
      await createMutation.mutateAsync(payload);
    }

    handleCloseDialog();
  };

  const handleDelete = (item) => {
    setEntityToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!entityToDelete?.uuid) return;
    await deleteMutation.mutateAsync(entityToDelete.uuid);
    setDeleteConfirmOpen(false);
    setEntityToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEntityToDelete(null);
  };

  return {
    data,
    plans,
    isLoading:
      isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    selectedPlan,
    dialogOpen,
    editMode,
    selectedConstructionType,
    deleteConfirmOpen,
    entityToDelete,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handlePlanChange,
    handleEdit,
    handleSave,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
